// ---- Cinematic runtime (plan 26) ----
// The orchestrator both hosts mount: pure timeline + puppet stage + shot solver
// + letterbox UI, wired behind one CineCtx seam. THE HOST OWNS THE STAGE WORLD
// (game: the swapChain build/teardown in src/cinematic/index.ts; studio: the
// #/cine view over worldstage) — the runtime only needs a parent for puppets,
// a surface function, and the camera. That split keeps the one-active-map
// problem entirely in host code.
//
// Lifecycle: playCinematic(doc, ctx) → host drives tick(dt, t) every frame →
// `done` resolves ("finished" | "skipped") → host fades, THEN calls dispose().

import type * as THREE from "three";
import type { CinematicDoc } from "../content/schemas.ts";
import { makeTimeline, CINE_TUNING, type TimelineOut } from "./timeline.ts";
import { buildPuppets, drivePuppets, resyncPuppets, disposePuppets, type CineStageCtx } from "./stage.ts";
import { makeCineCamera } from "./camera.ts";
import { mountCineUi, type CineUi } from "./ui.ts";
import { MOVE_CLIPS } from "../anim/clips.ts";
import { PUNCH_DUR } from "../player.ts";

export type CineCtx = CineStageCtx & {
  camera: THREE.PerspectiveCamera;
  /** Drive the host's fade element for the doc's `fade` actions (optional). */
  fade?(dir: "out" | "in", ms: number): void;
  /** Mount the letterbox/dialogue DOM (default: doc.letterbox ?? true). */
  ui?: boolean;
};

export type CinePlayback = {
  tick(dt: number, t: number): void;
  /** Complete the streaming line / advance the beat (click). */
  click(): void;
  /** End the whole cinematic. Honors doc.skippable unless `force` (studio). */
  skip(force?: boolean): boolean;
  jumpToShot(i: number): void;
  setPaused(p: boolean): void;
  paused(): boolean;
  shotCount: number;
  shotIndex(): number;
  done: Promise<"finished" | "skipped">;
  /** Remove puppets + UI. Host calls AFTER `done` (behind its fade). NOT the
   *  stage world — the host built that and tears it down itself. */
  dispose(): void;
};

/** THE gesture-length resolution — every makeTimeline caller (playback here,
 *  the studio browse estimate, the headless inspector) passes this one, so
 *  their timings can't drift from what actually plays. */
export const cineClipDuration = (clip: string): number =>
  clip === "primary" ? PUNCH_DUR : MOVE_CLIPS[clip]?.duration ?? CINE_TUNING.defaultClipSec;

export function playCinematic(doc: CinematicDoc, ctx: CineCtx): CinePlayback {
  const timeline = makeTimeline(doc, { clipDuration: cineClipDuration });
  const puppets = buildPuppets(doc, ctx);
  const cineCam = makeCineCamera(ctx.camera, {
    actor: (id) => {
      const p = puppets.get(id);
      if (!p) return null;
      return { x: p.group.position.x, y: p.feetY, z: p.group.position.z, ry: p.group.rotation.y };
    },
    groundY: (x, z) => ctx.surfaceY(x, z, 0),
  });

  const names = new Map<string, string>();
  for (const p of puppets.values()) names.set(p.actorId, p.name);
  const nameOf = (id: string): string => names.get(id) ?? id;

  let resolveDone!: (v: "finished" | "skipped") => void;
  const done = new Promise<"finished" | "skipped">((r) => { resolveDone = r; });
  let ended = false;
  let isPaused = false;
  let lastOut: TimelineOut | null = null;

  const ui: CineUi | null =
    (ctx.ui ?? doc.letterbox ?? true)
      ? mountCineUi({
          skippable: doc.skippable ?? false,
          onAdvance: () => click(),
          onSkip: () => skip(),
        })
      : null;
  ui?.animateIn();

  function finish(how: "finished" | "skipped"): void {
    if (ended) return;
    ended = true;
    void ui?.animateOut();
    resolveDone(how);
  }

  function tick(dt: number, t: number): void {
    if (ended || isPaused) return;
    const out = timeline.tick(dt);
    lastOut = out;
    drivePuppets(puppets, out, dt, t, ctx);
    cineCam.apply(out.camera, out.shotT, doc.shots[out.shotIndex]?.duration, dt, out.cameraCut);
    ui?.setLine(out.line, nameOf);
    for (const f of out.fades) ctx.fade?.(f.dir, f.ms);
    if (out.done) finish("finished");
  }

  function click(): void {
    if (!ended) timeline.click();
  }

  function skip(force = false): boolean {
    if (ended) return true;
    if (!force && !doc.skippable) return false;
    finish("skipped");
    return true;
  }

  function jumpToShot(i: number): void {
    if (ended) return;
    timeline.jumpToShot(i);
    const out = timeline.tick(0);
    lastOut = out;
    resyncPuppets(puppets, out, ctx);
  }

  return {
    tick,
    click,
    skip,
    jumpToShot,
    setPaused: (p) => { isPaused = p; },
    paused: () => isPaused,
    shotCount: doc.shots.length,
    shotIndex: () => lastOut?.shotIndex ?? 0,
    done,
    dispose() {
      ended = true;
      disposePuppets(puppets, ctx);
      ui?.dispose();
    },
  };
}
